import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWeb3 } from '../context/Web3Context';
import { useWriteContract, useWaitForTransactionReceipt, useReadContract } from 'wagmi';
import { formatAddress, formatETH } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { parseEther, formatEther } from 'viem';
import { 
    Trophy, 
    Crown,
    Medal,
    Star,
    Flame,
    Calendar,
    Users,
    Clock,
    Coins,
    Target,
    Zap,
    ChevronRight,
    Timer,
    Award,
    TrendingUp,
    Loader2,
    ExternalLink
} from 'lucide-react';
import { formatDistanceToNow, format, addDays } from 'date-fns';
import { TOURNAMENT_CONTRACT_ADDRESS, TOURNAMENT_CONTRACT_ABI, TOURNAMENT_STATUS, TOURNAMENT_TYPE } from '../contracts/tournamentConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tournament status badge
function TournamentStatusBadge({ status }) {
    const statusConfig = {
        active: { color: '#00FF66', label: 'Active', icon: Flame },
        upcoming: { color: '#FFD700', label: 'Upcoming', icon: Clock },
        completed: { color: '#9B6FE8', label: 'Completed', icon: Trophy }
    };
    
    const config = statusConfig[status] || statusConfig.active;
    const Icon = config.icon;
    
    return (
        <Badge style={{ backgroundColor: `${config.color}20`, color: config.color }}>
            <Icon className="w-3 h-3 mr-1" />
            {config.label}
        </Badge>
    );
}

// Tournament Card
function TournamentCard({ tournament, onJoin, onJoinAsTeam, joiningId, isConnected, userTeams }) {
    const { t } = useTranslation();
    const [showTeamSelect, setShowTeamSelect] = useState(false);
    const timeLeft = tournament.end_date 
        ? formatDistanceToNow(new Date(tournament.end_date), { addSuffix: true })
        : null;
    
    const progress = tournament.current_participants 
        ? (tournament.current_participants / tournament.max_participants) * 100
        : 0;
    
    const isJoining = joiningId === tournament.id;
    
    return (
        <Card className="bg-white/[0.02] border-white/5 hover:border-[#00FF66]/30 transition-all group card-hover">
            <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                    <div>
                        <TournamentStatusBadge status={tournament.status} />
                        <CardTitle className="text-lg text-white mt-2 group-hover:text-[#00FF66] transition-colors">
                            {tournament.name}
                        </CardTitle>
                        <CardDescription className="text-white/50">
                            {tournament.description}
                        </CardDescription>
                    </div>
                    <div className="text-right">
                        <div className="text-2xl font-bold text-[#FFD700]">
                            {tournament.prize_pool || 0}
                        </div>
                        <div className="text-xs text-white/40">ETH Prize Pool</div>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Tournament Stats */}
                <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-2 rounded-lg bg-white/[0.03]">
                        <Users className="w-4 h-4 text-[#00FF66] mx-auto mb-1" />
                        <div className="text-white font-semibold">
                            {tournament.current_participants || 0}/{tournament.max_participants || '∞'}
                        </div>
                        <div className="text-xs text-white/40">Players</div>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-white/[0.03]">
                        <Target className="w-4 h-4 text-[#FFD700] mx-auto mb-1" />
                        <div className="text-white font-semibold">
                            {tournament.min_bets || 5}
                        </div>
                        <div className="text-xs text-white/40">{t('tournament.minBets')}</div>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-white/[0.03]">
                        <Timer className="w-4 h-4 text-[#FF6B6B] mx-auto mb-1" />
                        <div className="text-white font-semibold text-sm">
                            {timeLeft || t('tournament.tbd')}
                        </div>
                        <div className="text-xs text-white/40">
                            {tournament.status === 'completed' ? t('tournament.ended') : t('tournament.remaining')}
                        </div>
                    </div>
                </div>
                
                {/* Participant Progress */}
                {tournament.max_participants && (
                    <div>
                        <div className="flex justify-between text-xs text-white/40 mb-1">
                            <span>{t('tournament.participants')}</span>
                            <span>{Math.round(progress)}% {t('tournament.filled')}</span>
                        </div>
                        <Progress value={progress} className="h-1.5 bg-white/10" />
                    </div>
                )}
                
                {/* Top 3 Preview */}
                {tournament.leaderboard && tournament.leaderboard.length > 0 && (
                    <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5">
                        <div className="text-xs text-white/40 mb-2">{t('tournament.currentTop3')}</div>
                        <div className="space-y-2">
                            {tournament.leaderboard.slice(0, 3).map((player, idx) => {
                                const icons = [Crown, Medal, Medal];
                                const colors = ['#FFD700', '#C0C0C0', '#CD7F32'];
                                const Icon = icons[idx];
                                return (
                                    <div key={`top-${player.address || idx}`} className="flex items-center gap-2">
                                        <Icon className="w-4 h-4" style={{ color: colors[idx] }} />
                                        <span className="text-white text-sm">{formatAddress(player.address)}</span>
                                        <span className="ml-auto text-white/60 text-sm">{player.wins || player.score} {tournament.type === 'volume' ? 'ETH' : t('tournament.wins')}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                
                {/* Action Buttons */}
                <div className="space-y-2">
                    <Button 
                        className={`w-full ${
                            tournament.status === 'active' 
                                ? 'bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52]' 
                                : 'bg-white/10 text-white hover:bg-white/20'
                        }`}
                        disabled={tournament.status === 'completed' || isJoining || !isConnected}
                        onClick={() => onJoin && onJoin(tournament)}
                        data-testid={`join-solo-${tournament.id}`}
                    >
                        {isJoining ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('tournament.joining')}</>
                        ) : tournament.status === 'active' ? (
                            <>{t('tournament.joinSolo')} {tournament.entry_fee > 0 ? `(${tournament.entry_fee} ETH)` : ''}</>
                        ) : tournament.status === 'upcoming' ? (
                            <>{t('tournament.registerInterest')}</>
                        ) : (
                            <>{t('tournament.viewResults')}</>
                        )}
                    </Button>
                    
                    {/* Team Join */}
                    {tournament.status === 'active' && isConnected && userTeams?.length > 0 && (
                        <>
                            {!showTeamSelect ? (
                                <Button 
                                    variant="outline"
                                    className="w-full border-[#9B6FE8]/30 text-[#9B6FE8] hover:bg-[#9B6FE8]/10"
                                    onClick={() => setShowTeamSelect(true)}
                                    disabled={isJoining}
                                    data-testid={`join-team-toggle-${tournament.id}`}
                                >
                                    <Users className="w-4 h-4 mr-2" />
                                    Join as Team
                                </Button>
                            ) : (
                                <div className="p-3 rounded-lg bg-[#9B6FE8]/10 border border-[#9B6FE8]/20 space-y-2">
                                    <div className="text-xs text-[#9B6FE8] font-medium">Select your team:</div>
                                    {userTeams.map(team => (
                                        <Button
                                            key={team.id}
                                            size="sm"
                                            className="w-full bg-[#9B6FE8] text-white hover:bg-[#8B5FD8] justify-start"
                                            onClick={() => { onJoinAsTeam?.(tournament, team.id); setShowTeamSelect(false); }}
                                            disabled={isJoining}
                                            data-testid={`join-team-${team.id}`}
                                        >
                                            <Users className="w-3 h-3 mr-2" />
                                            {team.name} ({team.members?.length || 0} members)
                                        </Button>
                                    ))}
                                    <Button
                                        size="sm" variant="ghost"
                                        className="w-full text-white/40"
                                        onClick={() => setShowTeamSelect(false)}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            )}
                        </>
                    )}

                    {/* Registered teams display */}
                    {tournament.registered_teams?.length > 0 && (
                        <div className="p-2 rounded-lg bg-[#9B6FE8]/5 border border-[#9B6FE8]/10">
                            <div className="text-xs text-[#9B6FE8]/60 mb-1">Teams registered:</div>
                            <div className="flex flex-wrap gap-1">
                                {tournament.registered_teams.map((t) => (
                                    <Badge key={t.id || t.name || t} className="bg-[#9B6FE8]/20 text-[#9B6FE8] text-xs">{t.name || formatAddress(t)}</Badge>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                
                {/* On-chain badge */}
                {tournament.on_chain_id && (
                    <div className="flex items-center justify-center gap-1 text-xs text-white/40">
                        <Zap className="w-3 h-3" />
                        On-chain Tournament
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// Sample tournaments data (will be replaced with API data)
const SAMPLE_TOURNAMENTS = [
    {
        id: '1',
        name: 'Weekly Win Streak Challenge',
        description: 'Win the most bets this week to claim the prize!',
        status: 'active',
        prize_pool: 0.5,
        current_participants: 24,
        max_participants: 50,
        min_bets: 5,
        start_date: new Date().toISOString(),
        end_date: addDays(new Date(), 5).toISOString(),
        leaderboard: [
            { address: '0x1234...5678', wins: 8 },
            { address: '0xabcd...efgh', wins: 6 },
            { address: '0x9876...4321', wins: 5 }
        ]
    },
    {
        id: '2',
        name: 'Monthly High Roller',
        description: 'Highest total stakes win big rewards!',
        status: 'upcoming',
        prize_pool: 2.0,
        current_participants: 0,
        max_participants: 100,
        min_bets: 10,
        start_date: addDays(new Date(), 3).toISOString(),
        end_date: addDays(new Date(), 33).toISOString(),
        leaderboard: []
    },
    {
        id: '3',
        name: 'Oracle Masters',
        description: 'Most successful UMA Oracle resolutions win!',
        status: 'completed',
        prize_pool: 1.0,
        current_participants: 45,
        max_participants: 50,
        min_bets: 3,
        start_date: addDays(new Date(), -30).toISOString(),
        end_date: addDays(new Date(), -2).toISOString(),
        leaderboard: [
            { address: '0xWinner...1234', wins: 12 },
            { address: '0xRunner...5678', wins: 10 },
            { address: '0xThird...9abc', wins: 8 }
        ]
    }
];

export function TournamentPage() {
    const { address, isConnected, chainId } = useWeb3();
    const [tournaments, setTournaments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [joiningId, setJoiningId] = useState(null);
    const [userTeams, setUserTeams] = useState([]);

    // On-chain join tournament
    const { 
        writeContract: joinOnChain, 
        data: joinHash,
        isPending: isJoinPending,
        error: joinError
    } = useWriteContract();
    
    const { isLoading: isJoinConfirming, isSuccess: isJoinSuccess } = 
        useWaitForTransactionReceipt({ hash: joinHash });

    useEffect(() => {
        fetchTournaments();
    }, []);
    
    // Fetch user teams
    useEffect(() => {
        if (address) {
            fetch(`${API_URL}/api/teams`)
                .then(r => r.ok ? r.json() : [])
                .then(teams => {
                    const myTeams = teams.filter(t => t.members?.includes(address.toLowerCase()));
                    setUserTeams(myTeams);
                })
                .catch(() => {});
        }
    }, [address]);
    
    // Handle join success
    useEffect(() => {
        if (isJoinSuccess && joinHash) {
            toast.success('Successfully joined tournament!', {
                description: 'Your entry has been recorded on-chain.'
            });
            fetchTournaments();
            setJoiningId(null);
        }
    }, [isJoinSuccess, joinHash]);
    
    // Handle join error
    useEffect(() => {
        if (joinError) {
            toast.error('Failed to join tournament', {
                description: joinError.shortMessage || 'Transaction failed'
            });
            setJoiningId(null);
        }
    }, [joinError]);

    const fetchTournaments = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/tournaments?limit=20`);
            if (response.ok) {
                const data = await response.json();
                // If no tournaments exist yet, use sample data
                if (data.length === 0) {
                    setTournaments(SAMPLE_TOURNAMENTS);
                } else {
                    setTournaments(data);
                }
            } else {
                // Fallback to sample data
                setTournaments(SAMPLE_TOURNAMENTS);
            }
        } catch (error) {
            console.error('Failed to fetch tournaments:', error);
            setTournaments(SAMPLE_TOURNAMENTS);
        } finally {
            setLoading(false);
        }
    };

    const handleJoinTournament = async (tournament) => {
        if (!isConnected || !address) {
            toast.error('Connect wallet to join');
            return;
        }
        
        const tournamentId = tournament.on_chain_id || tournament.id;
        setJoiningId(tournamentId);
        
        // Check if tournament has on-chain ID (deployed tournament)
        if (tournament.on_chain_id) {
            try {
                // Join on-chain
                await joinOnChain({
                    address: TOURNAMENT_CONTRACT_ADDRESS,
                    abi: TOURNAMENT_CONTRACT_ABI,
                    functionName: 'joinTournament',
                    args: [BigInt(tournament.on_chain_id)],
                    value: tournament.entry_fee ? parseEther(tournament.entry_fee.toString()) : BigInt(0)
                });
            } catch (error) {
                toast.error('Failed to join tournament');
                setJoiningId(null);
            }
        } else {
            // Join via API (off-chain tournament)
            try {
                const response = await fetch(`${API_URL}/api/tournaments/${tournamentId}/join?address=${address}`, {
                    method: 'POST'
                });
                const result = await response.json();
                
                if (result.success) {
                    toast.success('Joined tournament!');
                    fetchTournaments();
                } else {
                    toast.error(result.error || 'Failed to join');
                }
            } catch (error) {
                toast.error('Failed to join tournament');
            } finally {
                setJoiningId(null);
            }
        }
    };

    const handleJoinAsTeam = async (tournament, teamId) => {
        if (!isConnected || !address) {
            toast.error('Connect wallet to join');
            return;
        }
        
        const tournamentId = tournament.on_chain_id || tournament.id;
        setJoiningId(tournamentId);
        
        try {
            const response = await fetch(
                `${API_URL}/api/tournaments/${tournamentId}/join?address=${address}&team_id=${teamId}`,
                { method: 'POST' }
            );
            const result = await response.json();
            
            if (result.success) {
                toast.success('Team joined tournament!');
                fetchTournaments();
            } else {
                toast.error(result.error || 'Failed to join');
            }
        } catch (error) {
            toast.error('Failed to join tournament');
        } finally {
            setJoiningId(null);
        }
    };

    const filteredTournaments = tournaments.filter(t => 
        filter === 'all' || t.status === filter
    );

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 page-enter" data-testid="tournament-page">
            {/* Header */}
            <div className="text-center mb-8 animate-fade-in-down">
                <div className="flex items-center justify-center gap-3 mb-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FFD700]/20 to-[#FF6B6B]/20 flex items-center justify-center">
                        <Trophy className="w-7 h-7 text-[#FFD700]" />
                    </div>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                    <span className="text-[#FFD700]">Tournaments</span> & Competitions
                </h1>
                <p className="text-white/50 max-w-md mx-auto">
                    Compete against other players for prizes. Win streaks, volume challenges, and more!
                </p>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <Card className="bg-white/[0.02] border-white/5 card-hover animate-fade-in-up delay-1">
                    <CardContent className="p-4 text-center">
                        <Trophy className="w-5 h-5 text-[#FFD700] mx-auto mb-2" />
                        <div className="text-2xl font-bold text-white stat-number">{tournaments.length}</div>
                        <div className="text-xs text-white/40">Total Tournaments</div>
                    </CardContent>
                </Card>
                <Card className="bg-white/[0.02] border-white/5 card-hover animate-fade-in-up delay-2">
                    <CardContent className="p-4 text-center">
                        <Flame className="w-5 h-5 text-[#00FF66] mx-auto mb-2" />
                        <div className="text-2xl font-bold text-white stat-number">
                            {tournaments.filter(t => t.status === 'active').length}
                        </div>
                        <div className="text-xs text-white/40">Active Now</div>
                    </CardContent>
                </Card>
                <Card className="bg-white/[0.02] border-white/5 card-hover animate-fade-in-up delay-3">
                    <CardContent className="p-4 text-center">
                        <Coins className="w-5 h-5 text-[#9B6FE8] mx-auto mb-2" />
                        <div className="text-2xl font-bold text-white stat-number">
                            {tournaments.reduce((sum, t) => sum + (t.prize_pool || 0), 0).toFixed(1)}
                        </div>
                        <div className="text-xs text-white/40">ETH in Prizes</div>
                    </CardContent>
                </Card>
                <Card className="bg-white/[0.02] border-white/5 card-hover animate-fade-in-up delay-4">
                    <CardContent className="p-4 text-center">
                        <Users className="w-5 h-5 text-[#FF6B6B] mx-auto mb-2" />
                        <div className="text-2xl font-bold text-white stat-number">
                            {tournaments.reduce((sum, t) => sum + (t.current_participants || 0), 0)}
                        </div>
                        <div className="text-xs text-white/40">Total Players</div>
                    </CardContent>
                </Card>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2 animate-fade-in delay-5">
                {['all', 'active', 'upcoming', 'completed'].map(status => (
                    <Button
                        key={status}
                        variant={filter === status ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setFilter(status)}
                        className={`btn-press ${filter === status 
                            ? 'bg-[#00FF66] text-[#0A0A0C]' 
                            : 'border-white/10 text-white/60'
                        }`}
                    >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Button>
                ))}
            </div>

            {/* Tournament Grid */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {Array.from({ length: 4 }, (_, i) => `tour-skel-${i}`).map((sk) => (
                        <Skeleton key={sk} className="h-80 bg-white/5" />
                    ))}
                </div>
            ) : filteredTournaments.length === 0 ? (
                <Card className="bg-white/[0.02] border-white/5">
                    <CardContent className="p-12 text-center">
                        <Trophy className="w-12 h-12 text-white/20 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-white mb-2">No Tournaments Found</h3>
                        <p className="text-white/50">
                            {filter !== 'all' 
                                ? `No ${filter} tournaments at the moment.`
                                : 'Check back soon for new competitions!'
                            }
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {filteredTournaments.map(tournament => (
                        <TournamentCard 
                            key={tournament.id} 
                            tournament={tournament} 
                            onJoin={handleJoinTournament}
                            onJoinAsTeam={handleJoinAsTeam}
                            joiningId={joiningId}
                            isConnected={isConnected}
                            userTeams={userTeams}
                        />
                    ))}
                </div>
            )}

            {/* How Tournaments Work */}
            <Card className="bg-white/[0.02] border-white/5 mt-8">
                <CardHeader>
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                        <Award className="w-5 h-5 text-[#FFD700]" />
                        How Tournaments Work
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="text-center">
                            <div className="w-12 h-12 rounded-full bg-[#00FF66]/20 flex items-center justify-center mx-auto mb-3">
                                <span className="text-[#00FF66] font-bold">1</span>
                            </div>
                            <h4 className="text-white font-semibold mb-1">Join Tournament</h4>
                            <p className="text-white/50 text-sm">
                                Register for active tournaments. Some may require minimum bets.
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="w-12 h-12 rounded-full bg-[#FFD700]/20 flex items-center justify-center mx-auto mb-3">
                                <span className="text-[#FFD700] font-bold">2</span>
                            </div>
                            <h4 className="text-white font-semibold mb-1">Compete & Win</h4>
                            <p className="text-white/50 text-sm">
                                Win bets during the tournament period to climb the leaderboard.
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="w-12 h-12 rounded-full bg-[#9B6FE8]/20 flex items-center justify-center mx-auto mb-3">
                                <span className="text-[#9B6FE8] font-bold">3</span>
                            </div>
                            <h4 className="text-white font-semibold mb-1">Claim Prizes</h4>
                            <p className="text-white/50 text-sm">
                                Top 3 players receive ETH prizes automatically at tournament end.
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Coming Soon Features */}
            <div className="mt-8 p-6 rounded-xl border border-[#00FF66]/30 bg-[#00FF66]/5">
                <div className="flex items-center gap-3 mb-3">
                    <Zap className="w-8 h-8 text-[#00FF66]" />
                    <div>
                        <h3 className="text-lg font-semibold text-white">On-Chain Tournament Contract Deployed!</h3>
                        <p className="text-white/50 text-sm">Secure, trustless prize pools on Base, Arbitrum & Polygon</p>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <a 
                        href={`https://basescan.org/address/${TOURNAMENT_CONTRACT_ADDRESS}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] transition-colors"
                    >
                        <span className="text-white text-sm">Base</span>
                        <ExternalLink className="w-4 h-4 text-white/40" />
                    </a>
                    <a 
                        href={`https://arbiscan.io/address/${TOURNAMENT_CONTRACT_ADDRESS}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] transition-colors"
                    >
                        <span className="text-white text-sm">Arbitrum</span>
                        <ExternalLink className="w-4 h-4 text-white/40" />
                    </a>
                    <a 
                        href={`https://polygonscan.com/address/${TOURNAMENT_CONTRACT_ADDRESS}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] transition-colors"
                    >
                        <span className="text-white text-sm">Polygon</span>
                        <ExternalLink className="w-4 h-4 text-white/40" />
                    </a>
                </div>
                <p className="text-xs text-white/30 mt-3 font-mono text-center">
                    Contract: {TOURNAMENT_CONTRACT_ADDRESS}
                </p>
            </div>
        </div>
    );
}
